import { Capacitor } from '@capacitor/core';
import { LOG_LEVEL, Purchases } from '@revenuecat/purchases-capacitor';

const TOPUP_100_PACK_ID = 'topup_100';
const RC_ANONYMOUS_PREFIX = '$RCAnonymousID:';

let didConfigure = false;
let configuredAppUserId = null;
let pendingConfiguration = null;

const getEnv = (key) => {
  const value = process.env[key];
  return typeof value === 'string' ? value.trim() : '';
};

const getPublicApiKey = () => getEnv('REACT_APP_REVENUECAT_ANDROID_API_KEY');

const getTopupSelection = () => ({
  offeringId: getEnv('REACT_APP_REVENUECAT_ANDROID_OFFERING_ID'),
  packageId: getEnv('REACT_APP_REVENUECAT_ANDROID_PACKAGE_ID'),
});

const isAnonymousRevenueCatId = (value) =>
  typeof value === 'string' && value.startsWith(RC_ANONYMOUS_PREFIX);

const assertNativeAndroid = () => {
  if (!Capacitor?.isNativePlatform?.() || Capacitor.getPlatform() !== 'android') {
    throw new Error('Native RevenueCat billing is only available in the Android app.');
  }
};

const assertPublicApiKey = () => {
  const apiKey = getPublicApiKey();
  if (!apiKey) {
    throw new Error(
      'Missing REACT_APP_REVENUECAT_ANDROID_API_KEY for the Android RevenueCat client.'
    );
  }
  return apiKey;
};

const findSelectedOffering = (offerings, offeringId) => {
  if (!offerings) return null;

  if (offeringId) {
    if (offerings.all?.[offeringId]) {
      return offerings.all[offeringId];
    }

    const byIdentifier = Object.values(offerings.all || {}).find(
      (offering) => offering?.identifier === offeringId
    );
    if (byIdentifier) {
      return byIdentifier;
    }

    const availableOfferingIds = Object.values(offerings.all || {})
      .map((offering) => offering?.identifier)
      .filter(Boolean)
      .join(', ');

    throw new Error(
      availableOfferingIds
        ? `RevenueCat offering "${offeringId}" was not found. Available offerings: ${availableOfferingIds}.`
        : `RevenueCat offering "${offeringId}" was not found and no offerings are currently available.`
    );
  }

  return offerings.current || null;
};

const findSelectedPackage = (offering, packageId) => {
  const availablePackages = offering?.availablePackages || [];

  if (packageId) {
    const selectedPackage = availablePackages.find(
      (pkg) =>
        pkg?.identifier === packageId ||
        pkg?.product?.identifier === packageId
    );

    if (selectedPackage) {
      return selectedPackage;
    }

    const availablePackageIds = availablePackages
      .map((pkg) => `${pkg?.identifier || '(no package id)'} -> ${pkg?.product?.identifier || '(no product id)'}`)
      .join(', ');

    throw new Error(
      availablePackageIds
        ? `RevenueCat package or product "${packageId}" was not found in offering "${offering?.identifier || 'current'}". Available packages: ${availablePackageIds}.`
        : `RevenueCat package or product "${packageId}" was not found in offering "${offering?.identifier || 'current'}".`
    );
  }

  if (availablePackages.length === 1) {
    return availablePackages[0];
  }

  if (offering?.lifetime) {
    return offering.lifetime;
  }

  throw new Error(
    'Unable to determine which RevenueCat package should be used for the Android token pack. Set REACT_APP_REVENUECAT_ANDROID_PACKAGE_ID.'
  );
};

const ensureConfiguredForUser = async (appUserID) => {
  assertNativeAndroid();
  const apiKey = assertPublicApiKey();

  if (!appUserID) {
    throw new Error('RevenueCat requires an authenticated user ID before purchasing.');
  }

  if (pendingConfiguration) {
    await pendingConfiguration;
  }

  if (didConfigure && configuredAppUserId === appUserID) {
    return;
  }

  pendingConfiguration = (async () => {
    await Purchases.setLogLevel({
      level:
        process.env.NODE_ENV === 'development' ? LOG_LEVEL.DEBUG : LOG_LEVEL.INFO,
    });

    if (!didConfigure) {
      await Purchases.configure({
        apiKey,
        appUserID,
      });
      didConfigure = true;
      configuredAppUserId = appUserID;
      return;
    }

    const { customerInfo } = await Purchases.logIn({ appUserID });
    configuredAppUserId =
      customerInfo?.originalAppUserId && !isAnonymousRevenueCatId(customerInfo.originalAppUserId)
        ? customerInfo.originalAppUserId
        : appUserID;
  })()
    .catch((error) => {
      if (!didConfigure) {
        configuredAppUserId = null;
      }
      throw error;
    })
    .finally(() => {
      pendingConfiguration = null;
    });

  await pendingConfiguration;
};

const getTopupPackage = async () => {
  const { offeringId, packageId } = getTopupSelection();
  const offerings = await Purchases.getOfferings();
  const offering = findSelectedOffering(offerings, offeringId);

  if (!offering) {
    throw new Error(
      offeringId
        ? `RevenueCat offering "${offeringId}" is not available for this user.`
        : 'No current RevenueCat offering is available for this user.'
    );
  }

  return findSelectedPackage(offering, packageId);
};

export const nativeBilling = {
  isSupported() {
    try {
      return Capacitor?.isNativePlatform?.() === true && Capacitor.getPlatform() === 'android';
    } catch (_) {
      return false;
    }
  },

  async syncUser(appUserID) {
    await ensureConfiguredForUser(appUserID);
  },

  async clearUser() {
    if (!this.isSupported() || !didConfigure) {
      return;
    }

    if (pendingConfiguration) {
      await pendingConfiguration;
    }

    await Purchases.logOut();
    configuredAppUserId = null;
  },

  async purchaseTokenPack({ packId, appUserID }) {
    if (packId !== TOPUP_100_PACK_ID) {
      throw new Error(`Unsupported native token pack "${packId}".`);
    }

    await ensureConfiguredForUser(appUserID);
    const aPackage = await getTopupPackage();
    const purchase = await Purchases.purchasePackage({
      aPackage,
    });

    return {
      ...purchase,
      channel: 'revenuecat',
    };
  },

  async getCustomerInfo({ appUserID }) {
    await ensureConfiguredForUser(appUserID);
    return Purchases.getCustomerInfo();
  },
};
